import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import { API } from '../App';

// Survey: up to 5 custom questions per game (Survey 1..5), scored 1..N. The observer
// sees each question during the test at the chosen point, without interrupting the
// timing. Answers are kept separate from all timing and benchmark data and appear
// in Reports > Dealer History (Dealer Survey History) and the dealer PDF.

const MAX_QUESTIONS = 5;

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#0f0f1a', display: 'flex' },
  main: { flex: 1, padding: '40px', overflowY: 'auto' },
  pageTitle: { color: '#ffffff', fontSize: '28px', fontWeight: 'bold', marginBottom: '8px' },
  pageSubtitle: { color: '#a0a0b0', fontSize: '14px', marginBottom: '28px', maxWidth: '780px', lineHeight: 1.5 },
  gameTab: {
    padding: '10px 20px', backgroundColor: '#0f3460', border: '2px solid #0f3460', borderRadius: '8px',
    color: '#a0a0b0', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginRight: '8px', marginBottom: '20px',
  },
  gameTabActive: {
    padding: '10px 20px', backgroundColor: '#0f3460', border: '2px solid #e94560', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', marginRight: '8px', marginBottom: '20px',
  },
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '20px 24px', marginBottom: '16px' },
  qHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' },
  slotBadge: {
    display: 'inline-block', padding: '4px 10px', borderRadius: '6px', backgroundColor: '#e94560',
    color: '#ffffff', fontSize: '12px', fontWeight: 'bold', letterSpacing: '1px', marginBottom: '10px',
  },
  qText: { color: '#ffffff', fontSize: '17px', fontWeight: 'bold', marginBottom: '8px', lineHeight: 1.4 },
  meta: { color: '#a0a0b0', fontSize: '13px', lineHeight: 1.6 },
  actions: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  primaryBtn: {
    padding: '10px 20px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '10px 20px', backgroundColor: '#0f3460', border: '1px solid #0f3460', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  dangerBtn: {
    padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #e94560', borderRadius: '8px',
    color: '#e94560', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer',
  },
  label: { color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px', display: 'block' },
  input: {
    padding: '10px 12px', backgroundColor: '#0f3460', border: '1px solid #0f3460', borderRadius: '8px',
    color: '#ffffff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  },
  formRow: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' },
  preview: {
    backgroundColor: '#0f1a33', borderRadius: '8px', padding: '10px 14px', color: '#d0d0e0',
    fontSize: '13px', marginBottom: '16px',
  },
  warning: {
    backgroundColor: 'rgba(255, 204, 0, 0.12)', border: '1px solid #ffcc00', borderRadius: '8px',
    color: '#ffcc00', fontSize: '13px', padding: '10px 14px', marginBottom: '16px', lineHeight: 1.5,
  },
  muted: { color: '#a0a0b0', fontSize: '13px' },
  error: { color: '#e94560', fontSize: '14px', marginBottom: '12px' },
  success: { color: '#00cc00', fontSize: '14px', marginBottom: '16px' },
};

// Points in a game's process: 0 = start of the round, k = right after the button
// on screen k-1 is tapped.
export function surveyPoints(game) {
  const forms = game?.form_config?.forms || [];
  if (!forms.length) return ['Start of the round'];
  return forms.map((f, i) => (i === 0 ? 'Start of the round' : `After "${forms[i - 1].time_button}"`));
}

function whenText(q, game) {
  if (q.ask_at_end) return 'the review screen after End Session';
  const points = surveyPoints(game);
  const point = points[q.trigger_step] || points[0];
  return `Round ${Number(q.after_round) + 1}, ${q.trigger_step === 0 ? 'at the start of the round' : point.replace('After', 'after')}`;
}

const EMPTY = { question_text: '', scale_max: 10, after_round: 5, trigger_step: 0, ask_at_end: true };

function SurveySetup() {
  const isAdmin = localStorage.getItem('admin_role') === 'admin';
  const token = localStorage.getItem('admin_token');
  const [games, setGames] = useState([]);
  const [game, setGame] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | question id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetch(`${API}/game-types`, { headers })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setGames(data);
          if (data.length) setGame(data[0]);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (game) load();
  }, [game]); // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setQuestions(null);
    setEditing(null);
    const res = await fetch(`${API}/admin/survey-questions?game_type_id=${game.id}`, { headers });
    const data = await res.json().catch(() => ({}));
    setQuestions(data.questions || []);
  }

  function flash(msg) {
    setMessage(msg);
    setTimeout(() => setMessage(''), 4000);
  }

  function startEdit(q) {
    setError('');
    if (q === 'new') {
      setForm(EMPTY);
      setEditing('new');
    } else {
      setForm({ question_text: q.question_text, scale_max: q.scale_max, after_round: q.after_round,
        trigger_step: q.trigger_step, ask_at_end: !!q.ask_at_end });
      setEditing(q.id);
    }
  }

  async function save() {
    setBusy(true);
    setError('');
    const body = {
      question_text: form.question_text,
      scale_max: Number(form.scale_max),
      after_round: Number(form.after_round),
      trigger_step: Number(form.trigger_step),
      ask_at_end: !!form.ask_at_end,
    };
    const isNew = editing === 'new';
    try {
      const res = await fetch(isNew ? `${API}/admin/survey-questions` : `${API}/admin/survey-questions/${editing}`, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(isNew ? { ...body, game_type_id: game.id } : body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        flash(isNew ? 'Question added' : data.replaced ? 'Question updated (new version started)' : 'Question updated');
        load();
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Could not save');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  async function remove(q) {
    if (!window.confirm(`Remove Survey ${q.slot}? Answers already given are kept in reports.`)) return;
    const res = await fetch(`${API}/admin/survey-questions/${q.id}`, {
      method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ active: false }),
    });
    if (res.ok) {
      flash('Question removed');
      load();
    }
  }

  const points = surveyPoints(game);
  const editingQ = questions && editing && editing !== 'new' ? questions.find(q => q.id === editing) : null;
  const meaningChanged = editingQ && editingQ.answer_count > 0 &&
    (form.question_text.trim() !== editingQ.question_text || Number(form.scale_max) !== editingQ.scale_max);

  function renderForm() {
    return (
      <div style={styles.card}>
        <div style={{ ...styles.qText, marginBottom: '16px' }}>{editing === 'new' ? 'New question' : `Edit Survey ${editingQ?.slot}`}</div>
        <div style={{ marginBottom: '16px' }}>
          <label style={styles.label}>Question</label>
          <textarea style={{ ...styles.input, width: '100%', maxWidth: '640px', minHeight: '64px' }}
            maxLength={200} value={form.question_text}
            placeholder="e.g. How well did the dealer engage with guests?"
            onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))} />
          <div style={styles.muted}>{form.question_text.length}/200</div>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={styles.label}>When to ask</label>
          <select style={{ ...styles.input, minWidth: '320px' }} value={form.ask_at_end ? 'end' : 'during'}
            onChange={e => setForm(f => ({ ...f, ask_at_end: e.target.value === 'end' }))}>
            <option value="end">At the end of the test (after End Session)</option>
            <option value="during">During the test, at a chosen round and point</option>
          </select>
        </div>
        <div style={styles.formRow}>
          <div>
            <label style={styles.label}>Point scale</label>
            <select style={{ ...styles.input, minWidth: '140px' }} value={form.scale_max}
              onChange={e => setForm(f => ({ ...f, scale_max: e.target.value }))}>
              {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <option key={n} value={n}>1 to {n}</option>)}
            </select>
          </div>
          {!form.ask_at_end && (
            <>
              <div>
                <label style={styles.label}>Ask after round</label>
                <input style={{ ...styles.input, width: '110px' }} type="number" min="0" max="200" value={form.after_round}
                  onChange={e => setForm(f => ({ ...f, after_round: e.target.value }))} />
              </div>
              <div>
                <label style={styles.label}>At this point</label>
                <select style={{ ...styles.input, minWidth: '240px' }} value={form.trigger_step}
                  onChange={e => setForm(f => ({ ...f, trigger_step: e.target.value }))}>
                  {points.map((p, i) => <option key={i} value={i}>{p}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
        <div style={styles.preview}>
          {form.ask_at_end ? (
            <>The observer scores this on the <b>review screen after tapping End Session</b>, together with any other
            questions. Nothing is saved until they confirm, and the scores are read-only afterwards.</>
          ) : (
            <>The observer will see this question in <b>{whenText({ after_round: Number(form.after_round) || 0, trigger_step: Number(form.trigger_step) }, game)}</b>.
            It appears below the timing buttons and never interrupts the timing. Anything not answered during the test
            is asked again on the review screen at the end.</>
          )}
        </div>
        {meaningChanged && (
          <div style={styles.warning}>
            This question already has {editingQ.answer_count} answer(s). Changing the wording or scale starts a new
            version in the same slot, so past answers keep the question they were given for (reports show them as
            separate columns).
          </div>
        )}
        {error && <div style={styles.error}>{error}</div>}
        <div style={styles.actions}>
          <button style={styles.primaryBtn} disabled={busy || !form.question_text.trim()} onClick={save}>
            {busy ? 'Saving...' : 'Save Question'}
          </button>
          <button style={styles.secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Survey" />
      <div style={styles.main}>
        <div style={styles.pageTitle}>Survey</div>
        <div style={styles.pageSubtitle}>
          Add up to {MAX_QUESTIONS} of your own questions to each game. The observer scores each one during the test,
          at the round and point you choose. Survey answers are kept completely separate from timing and benchmark
          data, and appear in Reports &gt; Dealer History and on the dealer PDF.
        </div>

        <div>
          {games.map(g => (
            <button key={g.id} style={game?.id === g.id ? styles.gameTabActive : styles.gameTab} onClick={() => setGame(g)}>
              {g.name}
            </button>
          ))}
        </div>

        {message && <div style={styles.success}>{message}</div>}
        {questions === null && <div style={styles.muted}>Loading...</div>}

        {questions && questions.map(q => (
          editing === q.id ? <React.Fragment key={q.id}>{renderForm()}</React.Fragment> : (
            <div key={q.id} style={styles.card}>
              <div style={styles.qHead}>
                <div>
                  <div style={styles.slotBadge}>SURVEY {q.slot}</div>
                  <div style={styles.qText}>{q.question_text}</div>
                  <div style={styles.meta}>
                    Scale 1 to {q.scale_max} · Asked {q.ask_at_end ? 'on' : 'in'} {whenText(q, game)} · {q.answer_count} answer(s)
                  </div>
                </div>
                {isAdmin && (
                  <div style={styles.actions}>
                    <button style={styles.secondaryBtn} onClick={() => startEdit(q)}>Edit</button>
                    <button style={styles.dangerBtn} onClick={() => remove(q)}>Remove</button>
                  </div>
                )}
              </div>
            </div>
          )
        ))}

        {questions && questions.length === 0 && editing !== 'new' && (
          <div style={{ ...styles.muted, marginBottom: '16px' }}>No survey questions for {game?.name} yet.</div>
        )}

        {isAdmin && questions && editing === 'new' && renderForm()}
        {isAdmin && questions && editing === null && questions.length < MAX_QUESTIONS && (
          <button style={styles.primaryBtn} onClick={() => startEdit('new')}>
            + Add Question ({questions.length} of {MAX_QUESTIONS})
          </button>
        )}
        {questions && questions.length >= MAX_QUESTIONS && (
          <div style={styles.muted}>This game has the maximum of {MAX_QUESTIONS} questions.</div>
        )}
      </div>
    </div>
  );
}

export default SurveySetup;
